/**
 * Vérification en direct de la disponibilité d'un nom d'application (§3.2).
 *
 * Deux exigences : un anti-rebond de 400 ms exactement, et l'annulation de la
 * requête précédente. Sans l'annulation, une réponse lente pour « mon-ap »
 * pourrait arriver après celle de « mon-app » et afficher un verdict portant
 * sur une saisie que l'utilisateur a déjà dépassée.
 */

import { validateSlug, type CheckSlugResultDto } from '@portail/shared';
import { useEffect, useState } from 'react';

import { checkSlug } from '../api';
import { messageOf } from './useAsync';

/** Délai d'anti-rebond imposé par le cahier des charges. */
export const SLUG_CHECK_DEBOUNCE_MS = 400;

/** Résultat de la vérification, dans l'un de ses cinq états. */
export type SlugAvailability =
  | { status: 'idle' }
  | { status: 'invalid'; reason: string }
  | { status: 'checking' }
  | { status: 'available' }
  /**
   * Le nom désigne une application de l'utilisateur : le déploiement est
   * autorisé et produira une nouvelle version. À distinguer d'un refus, sans
   * quoi le formulaire se bloquerait sur une action pourtant valide.
   */
  | { status: 'update'; reason: string }
  | { status: 'unavailable'; reason: string };

/**
 * Traduit la réponse du serveur en état d'affichage.
 *
 * Extraite du hook pour être testable : la distinction entre « indisponible »
 * et « déjà à vous » décide si le bouton de déploiement reste actif, et une
 * confusion entre les deux bloque l'utilisateur sur une action pourtant valide.
 */
export function mapCheckSlugResponse(response: CheckSlugResultDto): SlugAvailability {
  if (response.available && response.update) {
    return {
      status: 'update',
      reason: response.reason ?? 'Déployer mettra à jour cette application.',
    };
  }
  if (response.available) return { status: 'available' };
  return {
    status: 'unavailable',
    // Le motif vient du serveur : il est déjà rédigé pour l'utilisateur.
    reason: response.reason ?? 'Ce nom n’est pas disponible.',
  };
}

/**
 * Interroge `GET /api/apps/check-slug` après stabilisation de la saisie.
 * Le format du slug est d'abord validé localement, avec les mêmes règles que
 * le serveur : inutile d'appeler l'API pour un nom manifestement invalide.
 */
export function useSlugAvailability(slug: string, domain: string): SlugAvailability {
  const [result, setResult] = useState<SlugAvailability>({ status: 'idle' });

  useEffect(() => {
    if (!slug || !domain) {
      setResult({ status: 'idle' });
      return;
    }

    const format = validateSlug(slug);
    if (!format.valid) {
      setResult({ status: 'invalid', reason: format.message });
      return;
    }

    setResult({ status: 'checking' });

    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      checkSlug(slug, domain, controller.signal)
        .then((response: CheckSlugResultDto) => {
          setResult(mapCheckSlugResponse(response));
        })
        .catch((error: unknown) => {
          if (error instanceof DOMException && error.name === 'AbortError') return;
          setResult({ status: 'unavailable', reason: messageOf(error) });
        });
    }, SLUG_CHECK_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [slug, domain]);

  return result;
}
